import styled, { css } from 'styled-components';
import { Typography } from '../../../global/theme';
import { mobile_xl } from '../../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, HideScrollbar } from '../../../global/commonStyle';

const MainDiv = styled.div`
  display: flex;
  flex: auto;
  height: 100vh;
  overflow: hidden;
  .loading-animation {
    overflow: hidden;
  }
  .loading-animation::before {
    content: '';
    display: block;
    height: 100%;
    width: 100%;
    animation: loading 1s infinite;
    background: linear-gradient(to right, transparent, var(--background-tertiary), transparent);
  }

  @keyframes loading {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  ${mobile_xl(css`
    flex-direction: column;
    ${HideScrollbar}
  `)}
`;

const LeftDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  margin: var(--space-0) auto;
  padding: var(--space-24) var(--space-24) var(--space-0) var(--space-24);
  max-width: 1022px;
  ${HideScrollbar}
  ${mobile_xl(css`
    overflow: unset;
    padding-right: var(--space-0);
  `)}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-16);
`;
const Profile = styled.div`
  ${AlignCenter}
  gap: var(--space-16);
`;

const Name = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;

const First = styled.div`
  width: 87px;
  height: 10px;
  background-color: var(--background-muted);
  border-radius: var(--border-radius-6);
`;

const Desc = styled.div`
  width: 110px;
  height: 21px;
  background-color: var(--background-muted);
  border-radius: var(--border-radius-6);
`;
const Icon = styled.div`
  display: inline-flex;
`;

const Project = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
`;

const Title = styled.div`
  color: var(--text-primary);
  ${Typography.heading_md}
`;

const Cards = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-gap: var(--space-14);
  grid-template-columns: repeat(auto-fill, minmax(194px, 1fr));
  ${mobile_xl(css`
    grid-row-gap: var(--space-0);
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-12);
    overflow: hidden;
  `)}
`;

const FirstCard = styled.div`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-12);
  max-width: 240px;
  width: 100%;
  background-color: var(--background-primary);
  ${mobile_xl(css`
    max-width: 100%;
    width: auto;
    border: none;
    border-radius: var(--border-radius-0);
    border-bottom: 0.5px solid var(--border-primary);
    :last-child {
      border-bottom: none;
    }
  `)}
`;

const TopCard = styled.div`
  padding: var(--space-16);
  display: flex;
  flex-direction: column;
  gap: var(--space-36);
  .bottom {
    width: 112px;
    height: 8px;
  }
  .last-bottom {
    width: 171px;
    height: 6px;
  }
  ${mobile_xl(css`
    padding-bottom: var(--space-0);
    .last-bottom {
      display: none;
    }
  `)}
`;

const Detail = styled.div`
  display: flex;
  justify-content: space-between;
`;
const LeftSide = styled.div`
  display: flex;
  gap: var(--space-12);
  flex-direction: column;
`;

const Right = styled.div`
  width: 57px;
  height: 21px;
  background-color: var(--background-muted);
  border-radius: var(--border-radius-6);
`;
const BottomCard = styled.div`
  padding: var(--space-16);
  display: flex;
  border-top: 0.5px solid var(--border-primary);
  ${mobile_xl(css`
    border-top: none;
    padding: var(--space-12) var(--space-20) var(--space-16);
  `)}
`;
const Circle = styled.div`
  width: 22px;
  height: 22px;
  border-radius: var(--border-radius-full);
  border: 3px solid var(--background-primary);
  background: var(--background-muted);
  margin-left: -8px;
`;
const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
`;

const FileCards = styled.div`
  display: flex;
  gap: var(--space-14);
  flex-wrap: wrap;
  ${mobile_xl(css`
    gap: var(--space-8);
  `)}
`;

const FirstFile = styled.div`
  border-radius: var(--border-radius-12);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  max-width: 167px;
  width: 100%;
  ${mobile_xl(css`
    max-width: calc(50% - 6px);
  `)}
`;
const TopFile = styled.div`
  padding-block: var(--space-32);
  display: flex;
  justify-content: center;
  .fill-color {
    fill: var(--background-muted);
  }
`;

const BottomFile = styled.div`
  padding: var(--space-11);
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  border-top: 0.5px solid var(--border-primary);
  .top {
    width: 70px;
    height: 12px;
    border-radius: var(--border-radius-8);
  }
  .bottom {
    width: 120px;
    height: 8px;
    border-radius: var(--border-radius-8);
  }
`;

const Heading = styled.div`
  ${FlexBetween}
`;
const Link = styled.a`
  text-decoration: none;
  cursor: pointer;
  gap: var(--space-8);
  color: var(--brand-primary);
  ${Typography.body_md_medium}
`;
const Invoice = styled.div`
  display: flex;
  gap: var(--space-8);
  flex-direction: column;
  ${mobile_xl(css`
    display: none;
  `)}
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  border-style: hidden;
  border-radius: var(--border-radius-8);
  box-shadow: var(--border-outset);
  background: var(--background-primary);
  margin: var(--space-0) var(--space-0) var(--space-24) var(--space-1);
  .row {
    padding: var(--space-0);
  }
  tr {
    border-top: 0.5px solid var(--border-primary);
    ${FlexBetween}
    padding: var(--space-10) var(--space-12);
  }
  thead {
    tr {
      border-top: none;
      background: var(--background-tertiary);
      border-radius: var(--border-radius-8) var(--border-radius-8) var(--border-radius-0) var(--border-radius-0);
    }
  }
  th {
    width: 100%;
    ${FlexBetween}
    gap: var(--space-20);
    text-align: left;
    padding: var(--space-10) var(--space-0) var(--space-10) var(--space-20);

    .lineIcon {
      stroke: var(--background-muted);
    }
    p {
      margin: var(--space-0);
      ${Typography.body_md_medium}
    }

    text-transform: uppercase;
    color: var(--text-secondary);
    :last-child {
      border-right: 0;
      width: 18px;
    }
    &:first-child {
      padding: var(--space-10) var(--space-0) var(--space-10) var(--space-12);
    }
  }
  td {
    width: 100%;
  }
  tr td:nth-last-child(3) {
    text-align: right;
  }
  tr th:nth-last-child(3) {
    justify-content: end;
  }
  tr td:nth-last-child(2) {
    width: 89%;
  }
  tr td:last-child {
    width: 18px;
  }
  .third {
    padding-right: var(--space-20);
    width: 120px;
    text-align: right;
  }
  .first {
    width: 103px;
    height: 9px;
    border-radius: var(--border-radius-6);
  }
  .first-row {
    gap: var(--space-40);
  }
`;

const Second = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  .top {
    width: 80px;
    height: 9px;
    border-radius: var(--border-radius-6);
  }
  .bottom {
    width: 100px;
    height: 6px;
    border-radius: var(--border-radius-6);
  }
`;
const Third = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  align-items: flex-end;
  .top {
    width: 60px;
    height: 9px;
    border-radius: var(--border-radius-6);
  }
  .bottom {
    width: 85px;
    height: 6px;
    border-radius: var(--border-radius-6);
  }
`;
const Forth = styled.div`
  .top {
    width: 66px;
    height: 6px;
    border-radius: var(--border-radius-6);
  }
`;
const RightDiv = styled.div`
  width: 100%;
  max-width: 268px;
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  padding: var(--space-24) var(--space-24) var(--space-24) var(--space-16);
  border-left: 0.5px solid var(--border-primary);
  .secondBox {
    padding: var(--space-10) var(--space-12);
  }
  ${mobile_xl(css`
    border-left: none;
    max-width: 100%;
    padding: var(--space-16) var(--space-0);
  `)}
`;

const Topright = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
`;

const Box = styled.div`
  padding: var(--space-12);
  border: 0.5px solid var(--border-primary);
  background-color: var(--background-primary);
  border-radius: var(--border-radius-8);
  display: flex;
  flex-direction: column;
`;
const FirstMember = styled.div`
  padding-block: var(--space-12) var(--space-18);
  border-bottom: 0.5px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  gap: var(--space-7);
  :last-child {
    border-bottom: none;
    padding-bottom: var(--space-6);
  }
  :first-child {
    padding-top: var(--space-0);
  }
`;
const TopMember = styled.div`
  ${FlexBetween}
`;

const BottomMember = styled.div`
  width: 150px;
  height: 6px;
  border-radius: var(--border-radius-6);
  background-color: var(--background-muted);
`;
const LeftTop = styled.div`
  width: 80px;
  height: 8px;
  border-radius: var(--border-radius-6);
  background-color: var(--background-muted);
`;
const RightTop = styled.div`
  display: flex;
  gap: var(--space-8);
  .right {
    width: 49px;
    height: 19px;
    border-radius: var(--border-radius-6);
    background-color: var(--background-muted);
  }
`;
const FirstInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  border-bottom: 0.5px solid var(--border-primary);
  padding: var(--space-16) var(--space-0);
  :last-child {
    border-bottom: none;
    padding-bottom: var(--space-5);
  }
  :first-child {
    padding-top: var(--space-5);
  }
`;

const TopInfo = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BottomInfo = styled.div`
  width: 74px;
  height: 6px;
  border-radius: var(--border-radius-6);
  background-color: var(--background-muted);
`;
const LeftInfo = styled.div`
  width: 136px;
  height: 8px;
  border-radius: var(--border-radius-6);
  background-color: var(--background-muted);
`;

const RightInfo = styled.div`
  width: 44px;
  height: 8px;
  border-radius: var(--border-radius-6);
  background-color: var(--background-muted);
`;

export {
  MainDiv,
  LeftDiv,
  Header,
  Profile,
  Name,
  First,
  Desc,
  Icon,
  Project,
  Title,
  Cards,
  FirstCard,
  TopCard,
  BottomCard,
  Detail,
  LeftSide,
  Right,
  Circle,
  Files,
  FileCards,
  FirstFile,
  TopFile,
  BottomFile,
  Heading,
  Link,
  Invoice,
  Table,
  Second,
  Third,
  Forth,
  RightDiv,
  Topright,
  Box,
  FirstMember,
  BottomMember,
  TopMember,
  LeftTop,
  RightTop,
  FirstInfo,
  TopInfo,
  BottomInfo,
  LeftInfo,
  RightInfo
};
